import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import type { IconName } from "@repo/fika-design-system/icon"
import {
  getPublicationLocales,
  parseLocale,
} from "@repo/fika-shared/src/locale"
import { createMemo, createSignal } from "solid-js"
import { Form } from "@/designSystem/components/form"
import { TextField } from "@/designSystem/components/text-field"
import { ImageUpload } from "@/designSystem/patterns/image-upload"
import {
  LanguageSelector,
  TranslatableField,
  TranslatableFieldGroup,
} from "@/designSystem/patterns/translatable-field"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { imageUploadClass } from "./_publication-details-form.css"

export const PublicationDetailsFragment = graphql(`
  fragment PublicationDetailsFragment on Publication {
    id
    title
    description
    logoUrl
    locale
    additionalLanguages
  }
`)

interface PublicationDetailsFormProps {
  publication: FragmentOf<typeof PublicationDetailsFragment>
  onAfterSave?: () => void | Promise<void>
  title: string
  iconName: IconName
  id?: string
}

export function PublicationDetailsForm(props: PublicationDetailsFormProps) {
  const { LL } = useI18nContext()
  const publication = createFragment(
    PublicationDetailsFragment,
    () => props.publication,
  )

  const UpdatePublicationDetailsMutation = graphql(
    `mutation UpdatePublicationDetails($input: UpdatePublicationInput!) {
      updateCurrentPublication(input: $input) {
        ... on MutationUpdateCurrentPublicationSuccess {
          data {
            ...PublicationDetailsFragment
          }
        }
        ...ErrorFragment
      }
    }`,
    [PublicationDetailsFragment, ErrorFragment],
  )
  const [updatePublicationState, updatePublication] =
    createMutationWithErrorHandling(
      UpdatePublicationDetailsMutation,
      "updateCurrentPublication",
    )

  const [formTitle, setFormTitle] = createSignal(publication().title ?? "")
  const [formDescription, setFormDescription] = createSignal(
    publication().description ?? "",
  )

  const defaultBranding = createMemo(() => {
    return {
      logo: publication().logoUrl ?? getDefaultAvatar(publication().id),
    }
  })
  const [formLogo, setFormLogo] = createSignal(defaultBranding().logo)

  const publicationLocale = createMemo(() => parseLocale(publication().locale))
  const availableLocales = createMemo(() =>
    getPublicationLocales(
      publicationLocale(),
      publication().additionalLanguages,
    ),
  )

  const dirtySettings = createMemo(
    () =>
      formLogo() !== defaultBranding().logo ||
      formTitle() !== publication().title ||
      formDescription() !== publication().description,
  )
  const disabledSettings = createMemo(
    () =>
      !dirtySettings() ||
      [formTitle(), formDescription()].every(field => field.length === 0),
  )

  async function onSubmitSettings() {
    const state = await updatePublication({
      input: {
        logoUrl: formLogo(),
        title: formTitle(),
        description: formDescription(),
      },
    })
    if (state.success) {
      toast.success(LL().settings.informationSaved())
      await props.onAfterSave?.()
    }
  }

  return (
    <Form
      id={props.id}
      title={props.title}
      iconName={props.iconName}
      description={LL().blog.titleAndDescriptionDescription()}
      hue="amber"
      onSubmit={onSubmitSettings}
    >
      <TranslatableFieldGroup
        availableLocales={availableLocales()}
        defaultLocale={publicationLocale()}
      >
        <LanguageSelector />
        <TranslatableField
          value={formTitle()}
          onChange={setFormTitle}
          availableLocales={availableLocales()}
          defaultLocale={publicationLocale()}
          name="title"
          format="text"
        >
          {(value, onChange) => (
            <TextField value={value()} onChange={onChange}>
              <TextField.Label>{LL().post.title()}</TextField.Label>
              <TextField.Input placeholder={LL().blog.yourBlogTitle()} />
            </TextField>
          )}
        </TranslatableField>
        <TranslatableField
          value={formDescription()}
          onChange={setFormDescription}
          availableLocales={availableLocales()}
          defaultLocale={publicationLocale()}
          name="description"
          format="text"
        >
          {(value, onChange) => (
            <>
              <TextField value={value()} onChange={onChange}>
                <TextField.Label>{LL().blog.description()}</TextField.Label>
                <TextField.Input
                  placeholder={LL().blog.yourBlogDescription()}
                />
              </TextField>
            </>
          )}
        </TranslatableField>
      </TranslatableFieldGroup>
      <TextField>
        <TextField.Label>{LL().blog.logo()}</TextField.Label>
        <ImageUpload
          text={LL().blog.uploadLogo()}
          imageSrc={formLogo()}
          onUpload={setFormLogo}
          hue="amber"
          imgClass={imageUploadClass}
          uploadFolder={["publication", publication().id]}
        />
        <TextField.Description>
          {LL().blog.logoDescription()}
        </TextField.Description>
      </TextField>
      <Form.Save
        hue="amber"
        disabled={disabledSettings()}
        active={updatePublicationState.fetching}
      />
    </Form>
  )
}
